import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { IExpression } from '../../../models/configuration/plugin/IConfiguration';
import { IExpressionEvaluation, IExpressionValidationResult } from '../../../models/IExpressionValidation';
import expressionService from '../../../services/expressionService';
import { RootState } from "../../../state/store"
import { useSelector } from "react-redux";
import { replaceExpressionFieldNameByFieldValue } from "../../../utils/expressionEvaluationHelper";
import { useEnhancedFieldIdValuePairs } from "../../../hooks/useEnhancedFieldIdValuePairs";
import DtForm from "../../ui/form/DtForm";
import { FormField } from "../../ui/form/formTypes";
import colorTheme from '../../../assets/colorTheme';
import { DtDialog, DtStatusDialog } from '../../ui/dialog';
import { DtLoader } from '../../ui/loader';
import { DtLabel } from '../../ui/label';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';


interface ExpressionEditorProps {
    expression: IExpression;
    editorTitle?: string;
    onSave?: (updatedExpression: IExpression, editorMode: string) => void;
    onValidate?: (expValidationResult: IExpressionEvaluation) => void;
    editorMode: string | null;
    renderMode?: "flat" | "dialog";
    open?: boolean;
    onCloseDialog?: () => void; 
}

const initialexpressionEvaluation: IExpressionEvaluation = {
    error: "Initial Evaluation",
    evaluationResult: false,
    expression: ""
};

const initialExpressionState: IExpression = {
    ExpressionName: "",
    ExpressionString: "",
    ExpressionMessage: "",
    Enabled: true
}

enum Mode {
    Init = "initial",
    Add = 'add',
    Edit = 'edit',
    View = 'view'
}

const ExpressionEditor: React.FC<ExpressionEditorProps> = ({
    expression,
    editorTitle = "Expression Editor",
    onSave,
    onValidate,
    editorMode,
    renderMode = "flat", 
    open = false, 
    onCloseDialog
}) => {

    const expService = new expressionService();
    const [expressionState, setExpressionState] = useState<IExpression>(expression ? expression : initialExpressionState);
    const [expressionEvaluation, setExpressionEvaluation] = useState<IExpressionEvaluation>(initialexpressionEvaluation);
    const [mode, setMode] = useState<Mode>(Mode.Init)
    const appState = useSelector((state: RootState) => state.appSlice)
    const accessToken = useSelector((state) => appState.accessToken);
    const enhancedFieldIdValuePairs = useEnhancedFieldIdValuePairs();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [isSaveEnabled, setIsSaveEnabled] = useState<boolean>(false);
    const [formKey, setFormKey] = useState(0);
    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState<{ message: JSX.Element | null, severity: 'success' | 'error' | null }>({
        message: null,
        severity: null
    });

    useEffect(() => {
        if (expression)
            setExpressionState(expression);
        if (editorMode === 'add')
            setMode(Mode.Add);
        else if (editorMode === 'edit')
            setMode(Mode.Edit);
        else if (editorMode === 'view')
            setMode(Mode.View)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Initial load

    useEffect(() => {
        if (mode === Mode.Add) {
            setExpressionState(initialExpressionState);
        }
    }, [mode])

    useEffect(() => {
        if (expression)
            setExpressionState(expression);
        if (editorMode === 'add')
            setMode(Mode.Add);
        else if (editorMode === 'edit')
            setMode(Mode.Edit);
        else if (editorMode === 'view')
            setMode(Mode.View)
        // Clear validation result when expression or editor mode changes
        setValidationResult({ message: null, severity: null });
    }, [expression, editorMode])

    const previousExpString = useRef(expressionState.ExpressionString);

    const enableSave = useCallback(() => {
        if (expressionState.ExpressionString && expressionState.ExpressionString !== previousExpString.current) {
            previousExpString.current = expressionState.ExpressionString;
            return false;
        }
        if (expressionState.ExpressionName.length === 0)
            return false;
        if (expressionState.ExpressionString && expressionState.ExpressionString.length !== 0 && expressionEvaluation.error != null)
            return false;
        return true;
    }, [expressionState, expressionEvaluation]);

    useEffect(() => {
        if (expressionState || expressionEvaluation)
            setIsSaveEnabled(enableSave())
    }, [expressionState, expressionEvaluation, enableSave]);

    const handleClearExpressionString = () => {
        setExpressionState(prevState => ({
            ...prevState,
            ExpressionString: '',
        }));
        setFormKey(prev => prev + 1); 
        setValidationResult({ message: null, severity: null });
    };

    const handleFormChange = (fieldId: string, value: any) => {
        setExpressionState(prevState => ({
            ...prevState,
            [fieldId]: value,
        }));
    };

    const handleSave = () => {
        if (onSave) {
            onSave(expressionState, mode);
        }
    };

    const handleValidate = async () => {
        setValidating(true);
        const expressionResult: IExpressionEvaluation = await validateExpression(expressionState);
        setDialogOpen(true);

        if (expressionResult.error == null || expressionResult.error === '') {
            expressionResult.evaluationResult = mode === Mode.View
                ? (expression.Enabled && expressionResult.evaluationResult)
                : expressionResult.evaluationResult;
            setIsSaveEnabled(true);
            setValidationResult({
                severity: 'success',
                message: (
                    <>
                        <b>Expression is Valid</b> {expressionResult.evaluationResult ? <> &nbsp;(Returned: 'True')</> : <>&nbsp;(Returned: 'False')</>} 
                    </>
                )
            });
        } else {
            setIsSaveEnabled(false);
            setValidationResult({
                severity: 'error',
                message: (
                    <><b>Expression is NOT Valid!</b> <br /> Eval failed for expression '{expressionState.ExpressionString}' :
                        Expression contains errors or is not a valid formula. </>
                )
            });
        }
        setExpressionEvaluation({ ...expressionResult });

        if (onValidate) {
            onValidate(expressionResult);
        }
        setValidating(false);
    }

    const validateExpression = async (expression: IExpression) => {
        const expressionArray: Array<string> = [];
        let expString = expression.ExpressionString;
        expString = expString.replace(/\s*\n\s*/g, ' ');
        expString = replaceExpressionFieldNameByFieldValue(expString, enhancedFieldIdValuePairs);
        expString = expString.replace(/\[([^\]]+)\]/g, `""`);
        expressionArray.push(expString);
        var expValidationResult: IExpressionValidationResult = await expService.validateExpressions(accessToken, expressionArray);
        //Through Expression Editor Dialog, only one expression at a time is evaluated, hence filtering the first item
        return expValidationResult.result[0];
    }

    const fields: FormField[] = [
        {
            id: "ExpressionName",
            label: "Name*",
            type: "text",
            required: true,
            defaultValue: expressionState.ExpressionName,
            readOnly: mode === Mode.View,
            helperText:'Expression Name must be unique',
            onChange: (value) => handleFormChange("ExpressionName", value),
        },
        {
            id: "ExpressionMessage",
            label: "Description (Friendly)",
            type: "textarea",
            defaultValue: expressionState.ExpressionMessage,
            textareaRows: 2,
            readOnly: mode === Mode.View,
            onChange: (value) => handleFormChange("ExpressionMessage", value),
        },
        {
            id: "ExpressionString",
            label: "Expression (VB Syntax Recommended)",
            type: "textarea",
            defaultValue: expressionState.ExpressionString, // will be '' after clear
            readOnly: mode === Mode.View,
            onChange: (value) => handleFormChange("ExpressionString", value),
            inputProps: { style: { paddingBottom: "1.5rem" } }
        }
    ];

    const getExpressionEditorFormActions = ({
        onSave,
        isSaveEnabled,
    }: {
        onValidate: () => void;
        onSave: () => void;
        isSaveEnabled: boolean;
        expressionString: string;
    }) => (
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            {mode !== Mode.View && (
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!isSaveEnabled}
                    onClick={onSave}
                >
                    Save
                </Button>
            )}
        </Box>
    );

    // Main editor content
    const editorContent = (
        <>
            <Box sx={{ position: "relative" }}>
                <DtForm
                    key={formKey}
                    labelPosition='ontop'
                    fields={fields}
                    showSubmit={false}
                    showReset={false}
                    onSubmit={handleSave}
                />
                <Box
                    sx={{
                        position: "absolute",
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        p: 0.5,
                        display: "flex",
                    }}
                >
                    {mode !== Mode.View && (
                        <Button
                            variant="text"
                            size="small"
                            disabled={!expressionState?.ExpressionString || validating}
                            onClick={handleClearExpressionString}
                        >
                            Clear Expression
                        </Button>
                    )}
                    <Button
                        variant="text"
                        size="small"
                        disabled={!expressionState?.ExpressionString}
                        onClick={handleValidate}
                    >
                        {validating ? <Stack direction="row" alignItems='center' spacing={1}><DtLoader size='xtraSmall' />
                        <span> Validating...</span></Stack> : "Validate"}
                    </Button>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1}}>
                {validationResult.message && !validating && (
                    <Alert severity={validationResult.severity ?? 'info'} title={validationResult.severity === 'error' ? 'Validation Error' : 'Validation Success'}>
                        {validationResult.message}
                    </Alert>
                )}
                <Alert color='info' icon={false} sx={{ background: colorTheme.palette.background.default }}>
                    <DtLabel sx={{display:'flex', gap: 1, color: colorTheme.palette.primary.main}}><HelpOutlineIcon  fontSize="small" color="primary" /> Help!</DtLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2, color: 'text.primary'}}>
                        <Box> - Expression must be <b>validated</b> to enable save.</Box>
                        <Box> - Useful Process Fields <b>[CX.DOCUTECH.SELECTED.PACKAGE]</b> <b>[CX.DOCUTECH.DELIVERYTYPES]</b></Box>
                    </Box>
                </Alert>
            </Box>
        </>
    );

    if (renderMode === "dialog") {
        return (
            <DtDialog
                open={open}
                title={editorTitle}
                maxWidth="md"
                onClose={() => onCloseDialog && onCloseDialog()} // <-- use prop here
                actions={<>
                    <Button onClick={onCloseDialog} size='small' variant='outlined'>Close</Button>{getExpressionEditorFormActions({
                        onValidate: handleValidate,
                        onSave: handleSave,
                        isSaveEnabled,
                        expressionString: expressionState?.ExpressionString ?? ""
                    })}</>}
            >
                {editorContent}
            </DtDialog>
        );
    }

    // Flat mode (default)
    return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {editorContent}
        {getExpressionEditorFormActions({
            onValidate: handleValidate,
            onSave: handleSave,
            isSaveEnabled,
            expressionString: expressionState?.ExpressionString ?? ""
        })}
    </Box>;
};

export default ExpressionEditor;